import { useState, useEffect } from 'react';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { useIsMobile } from '@/hooks/use-mobile';

interface WeekData {
  weekNumber: number;
  displayDate: string;
  displayDateEnd?: string;
}

interface TennisWeekCarouselProps {
  weeks: WeekData[];
}

const TennisWeekCarousel = ({ weeks }: TennisWeekCarouselProps) => {
  const isMobile = useIsMobile();
  const [activePage, setActivePage] = useState(0);
  const [api, setApi] = useState<CarouselApi>();

  // Group weeks into pages of 8
  const pages: WeekData[][] = [];
  for (let i = 0; i < weeks.length; i += 8) {
    pages.push(weeks.slice(i, i + 8));
  }

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setActivePage(api.selectedScrollSnap());
    api.on('select', onSelect);
    onSelect();
    return () => { api.off('select', onSelect); };
  }, [api]);

  return (
    <div className="w-full flex flex-col items-center">
      <Carousel className="w-full max-w-5xl" setApi={setApi}>
        <CarouselContent className="-ml-2 md:-ml-4">
          {pages.map((page, pageIdx) => (
            <CarouselItem key={pageIdx} className="basis-full pl-2 md:pl-4">
              <div className="flex flex-col gap-2">
                {(() => {
                  const rows: WeekData[][] = [];
                  for (let i = 0; i < page.length; i += 4) {
                    rows.push(page.slice(i, i + 4));
                  }
                  return rows.map((row, rowIdx) => (
                    <div
                      key={rowIdx}
                      className={row.length < 4 ? 'flex justify-center gap-2' : 'grid grid-cols-4 gap-2 justify-items-center'}
                    >
                      {row.map((week) => (
                        <div key={week.weekNumber} className="flex flex-col items-center">
                          <div className={`${isMobile ? 'w-16' : 'w-20'} ${week.displayDateEnd ? (isMobile ? 'h-20' : 'h-24') : (isMobile ? 'h-16' : 'h-20')} rounded-lg bg-gradient-to-br from-primary to-primary/60 flex flex-col items-center justify-center text-primary-foreground font-bold shadow-lg hover:shadow-xl transition-shadow`}>
                            <span className="text-xs font-semibold">S{week.weekNumber}</span>
                            <span className="text-xs mt-1">{week.displayDate}</span>
                            {week.displayDateEnd && (
                              <span className="text-xs">{week.displayDateEnd}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      {pages.length > 1 && (
        <div className="flex justify-center gap-2 mt-3">
          {pages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => api?.scrollTo(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === activePage ? 'bg-primary' : 'bg-muted hover:bg-muted-foreground/30'}`}
              aria-label={`Página ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TennisWeekCarousel;
